import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import ReactMarkdown from "react-markdown";
import styles from "./Assessment.module.css";
import api from "../../api";
import { assessmentParam, inAssessment, withAssessment } from "./assessmentFilter";
import { AnswerReveal } from "./AnswerReveal";

const MCQWorkspace = () => {
  const { id, topicId, problemId } = useParams();
  const assessment = assessmentParam(useLocation().search);
  const navigate = useNavigate();
  const selectedModuleId = useSelector((state) => state.module.selectedModuleId);
  const effectiveTopicId = topicId || selectedModuleId;

  // Problem state
  const [problems, setProblems] = useState([]);
  const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selection state
  const [selectedOption, setSelectedOption] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const currentProblem = problems[currentProblemIndex];

  // Fetch problems on mount
  useEffect(() => {
    const fetchProblems = async () => {
      if (!effectiveTopicId) return;

      setLoading(true);
      try {
        const response = await api.get(`course/${effectiveTopicId}/o/`);
        // Opened from one assessment: only its questions.
        const data = (response.data || []).filter((p) => inAssessment(p, assessment));
        setProblems(data);

        if (data.length > 0) {
          // If problemId is provided, find and select it
          if (problemId) {
            const index = data.findIndex(p => p.id === parseInt(problemId));
            if (index >= 0) setCurrentProblemIndex(index);
          }

          // Load first problem's submission
          const problem = problemId
            ? data.find(p => p.id === parseInt(problemId)) || data[0]
            : data[0];
          if (problem.submission_data?.[0]?.submission) {
            setSelectedOption(problem.submission_data[0].submission);
            setIsSubmitted(true);
          } else {
            setSelectedOption(null);
            setIsSubmitted(false);
          }
        }
      } catch (err) {
        setError(err.message || "Failed to load questions");
      } finally {
        setLoading(false);
      }
    };

    fetchProblems();
  }, [effectiveTopicId, problemId, assessment]);

  // Load selection when switching problems
  useEffect(() => {
    if (!currentProblem) return;

    const submission = currentProblem.submission_data?.[0];
    if (submission?.submission) {
      setSelectedOption(submission.submission);
      setIsSubmitted(true);
    } else {
      setSelectedOption(null);
      setIsSubmitted(false);
    }
  }, [currentProblemIndex, currentProblem]);

  // Once submitted the answer is final: the correct one is shown and nothing changes.
  const answerKey = currentProblem?.answer_key;
  const isFinal = Boolean(currentProblem?.final || answerKey);

  const handleOptionSelect = (optionId) => {
    if (isFinal) return;
    setSelectedOption(optionId);
    setIsSubmitted(false);
  };

  const handleSubmit = async () => {
    if (!currentProblem || !effectiveTopicId || isSubmitting || !selectedOption) return;

    setIsSubmitting(true);
    try {
      const response = await api.post(`course/${effectiveTopicId}/o/${currentProblem.id}/`, {
        submission: selectedOption,
      });
      setIsSubmitted(true);

      // Update problems state: final, with the correct answer to show.
      setProblems(prev => prev.map((p, i) =>
        i === currentProblemIndex
          ? { ...p, submission_data: [{ submission: selectedOption }], submitted: true,
              final: true, answer_key: response.data?.answer_key || p.answer_key }
          : p
      ));
    } catch (err) {
      console.error("Failed to submit:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrevious = async () => {
    if (currentProblemIndex > 0) {
      setCurrentProblemIndex(prev => prev - 1);
    }
  };

  const handleNext = async () => {
    if (currentProblemIndex < problems.length - 1) {
      setCurrentProblemIndex(prev => prev + 1);
    }
  };

  const handleBack = () => {
    navigate(withAssessment(`/dashboard/workspace/${id}/assessment/${effectiveTopicId}`, assessment));
  };

  if (loading) {
    return <div className={styles.workspace}>Loading...</div>;
  }

  if (error) {
    return <div className={styles.workspace}>Error: {error}</div>;
  }

  if (!currentProblem) {
    return <div className={styles.workspace}>No questions found</div>;
  }

  const options = currentProblem.mcq_answers || [];

  return (
    <div className={styles.workspace}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backButton} onClick={handleBack}>
            ←
          </button>
          <span className={styles.problemTitle}>
            {currentProblem.description?.slice(0, 50) || `Question ${currentProblemIndex + 1}`}
          </span>
          <span className={styles.mcqBadge}>MCQ</span>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.questionNav}>
            <button
              className={styles.questionNavBtn}
              onClick={handlePrevious}
              disabled={currentProblemIndex === 0}
            >
              ←
            </button>
            <span className={styles.questionNavText}>
              Question {currentProblemIndex + 1} of {problems.length}
            </span>
            <button
              className={styles.questionNavBtn}
              onClick={handleNext}
              disabled={currentProblemIndex === problems.length - 1}
            >
              →
            </button>
          </div>
        </div>
      </header>

      {/* Two-Pane Layout */}
      <div className={styles.mcqPaneContainer}>
        {/* Question Pane */}
        <div className={styles.problemPane}>
          <div className={styles.paneTabs}>
            <div className={`${styles.paneTab} ${styles.paneTabActive}`}>
              Question
            </div>
          </div>
          <div className={styles.problemContent}>
            <div className={styles.mcqQuestionContent}>
              <ReactMarkdown>{currentProblem.description}</ReactMarkdown>
            </div>
          </div>
        </div>

        {/* Options Pane */}
        <div className={styles.mcqOptionsPane}>
          <div className={styles.mcqOptionsHeader}>
            <span className={styles.editorLabel}>Select an Answer</span>
            <span className={isSubmitted ? styles.savedIndicator : styles.unsavedIndicator}>
              {isSubmitted ? "● Submitted" : selectedOption ? "○ Not submitted" : ""}
            </span>
          </div>

          <div className={styles.mcqOptionsContent}>
            <div className={styles.mcqOptionsList}>
              {options.map((option, index) => {
                const isSelected = selectedOption === option.id;
                const letter = String.fromCharCode(65 + index); // A, B, C, D...
                const isCorrect = answerKey && answerKey.correct_option_id === option.id;
                const markStyle = !answerKey ? undefined
                  : isCorrect ? { borderColor: "#1f9d4d", background: "#f1faf4", cursor: "default" }
                  : isSelected ? { borderColor: "#e3a0a0", background: "#fdf3f3", cursor: "default" }
                  : { cursor: "default", opacity: 0.75 };

                return (
                  <div
                    key={option.id}
                    className={`${styles.mcqOption} ${isSelected ? styles.mcqOptionSelected : ""}`}
                    style={markStyle}
                    onClick={() => handleOptionSelect(option.id)}
                    data-testid={isCorrect ? "correct-option" : undefined}
                  >
                    <div className={`${styles.mcqOptionLetter} ${isSelected ? styles.mcqOptionLetterSelected : ""}`}>
                      {letter}
                    </div>
                    <div className={styles.mcqOptionText}>
                      <ReactMarkdown>{option.option}</ReactMarkdown>
                    </div>
                    {answerKey && isCorrect && (
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#1f7a45", whiteSpace: "nowrap" }}>
                        {isSelected ? "✓ Your answer · correct" : "✓ Correct answer"}
                      </span>
                    )}
                    {answerKey && isSelected && !isCorrect && (
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#c93c3c", whiteSpace: "nowrap" }}>✗ Your answer</span>
                    )}
                    {!answerKey && isSelected && (
                      <div className={styles.mcqOptionCheck}>✓</div>
                    )}
                  </div>
                );
              })}
            </div>
            {answerKey?.explanation && (
              <AnswerReveal title="Explanation">
                <ReactMarkdown>{answerKey.explanation}</ReactMarkdown>
              </AnswerReveal>
            )}
          </div>

          <div className={styles.mcqOptionsFooter}>
            <div className={styles.mcqHint}>
              {isFinal ? "Your answer is final." : "Submitting is final and shows the correct answer."}
            </div>
            <button
              className={`${styles.editorBtn} ${styles.submitBtn}`}
              onClick={handleSubmit}
              disabled={isSubmitting || isSubmitted || !selectedOption}
            >
              {isSubmitting ? "Submitting..." : isSubmitted ? "Submitted" : "Submit Answer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MCQWorkspace;
